import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaUser, FaClock, FaArrowLeft, FaShare, FaFacebookF, FaTwitter, FaLinkedinIn, FaTag } from 'react-icons/fa';
import SEO from '../components/SEO.jsx';
import { blogPosts, getBlogContent } from '../data/blogPosts.jsx';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const post = blogPosts.find(p => p.slug === slug);
  const content = getBlogContent(slug);

  if (!post || !content) {
    navigate('/blog');
    return null;
  }

  const shareUrl = `https://sutrasync.com/blog/${post.slug}`;

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${post.title}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`
    };
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt,
    "image": `https://sutrasync.com${post.image}`,
    "datePublished": new Date(post.date).toISOString(),
    "dateModified": new Date(post.date).toISOString(),
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "Sutrasync",
      "logo": {
        "@type": "ImageObject",
        "url": "https://sutrasync.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": shareUrl
    }
  };

  return (
    <>
      <SEO 
        title={`${post.title} | Sutrasync Blog`}
        description={post.excerpt}
        keywords={post.tags.join(', ')}
        url={shareUrl}
        image={`https://sutrasync.com${post.image}`}
        structuredData={articleStructuredData}
        type="article"
      />

      <div className="pt-16 md:pt-20 min-h-screen bg-white">
        {/* Enhanced Hero Banner Section */}
        <motion.section 
          className="relative min-h-[85vh] bg-gradient-to-br from-[#22446b] via-[#2d5a8a] to-[#338edc] text-white overflow-hidden flex items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          {/* Animated Background Image with Parallax */}
          <motion.div 
            className="absolute inset-0"
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#22446b]/95 via-[#2d5a8a]/95 to-[#338edc]/95 z-10" />
            <motion.img 
              src={post.image} 
              alt={post.title}
              className="w-full h-full object-cover"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.3 }}
              transition={{ duration: 1.2, delay: 0.3 }}
            />
            {/* Animated Gradient Overlay */}
            <motion.div 
              className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50 z-10"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
            />
          </motion.div>

          {/* Animated Geometric Patterns */}
          <div className="absolute inset-0 z-10 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="post-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
                  <motion.circle 
                    cx="40" 
                    cy="40" 
                    r="2" 
                    fill="white"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 0.6 }}
                    transition={{ duration: 0.5, delay: 0.8 }}
                  />
                  <motion.path 
                    d="M0 40 L80 40 M40 0 L40 80" 
                    stroke="white" 
                    strokeWidth="0.5"
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ pathLength: 1, opacity: 0.3 }}
                    transition={{ duration: 1.5, delay: 1 }}
                  />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#post-pattern)" />
            </svg>
          </div>

          {/* Floating Particles */}
          <div className="absolute inset-0 z-10 overflow-hidden pointer-events-none">
            {[...Array(30)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -40, 0],
                  opacity: [0.1, 0.6, 0.1],
                  scale: [1, 1.5, 1]
                }}
                transition={{
                  duration: 4 + Math.random() * 3,
                  repeat: Infinity,
                  delay: Math.random() * 3,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>

          {/* Content Container */}
          <div className="container mx-auto px-6 lg:px-8 relative z-20">
            <div className="max-w-5xl mx-auto">
              {/* Back Button with Animation */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Link 
                  to="/blog"
                  className="inline-flex items-center gap-3 text-white/70 hover:text-white mb-12 transition-all duration-300 group"
                >
                  <motion.div
                    whileHover={{ x: -5 }}
                    transition={{ duration: 0.3 }}
                  >
                    <FaArrowLeft className="text-xl" />
                  </motion.div>
                  <span className="font-semibold tracking-wide">Back to Blog</span>
                  <div className="h-px flex-grow bg-gradient-to-r from-white/20 to-transparent max-w-[100px] group-hover:max-w-[150px] transition-all duration-500" />
                </Link>
              </motion.div>

              {/* Category Badge with 3D Effect */}
              <motion.div 
                className="flex items-center gap-4 mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                <motion.span 
                  className="relative bg-white text-[#22446b] text-sm font-bold px-6 py-3 rounded-full uppercase tracking-widest shadow-2xl"
                  whileHover={{ scale: 1.05, y: -2 }}
                  style={{
                    boxShadow: '0 10px 30px rgba(255,255,255,0.2)'
                  }}
                >
                  {post.category}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer rounded-full" />
                </motion.span>
                
                {/* Meta Info Pills */}
                <motion.div 
                  className="flex items-center gap-4 text-sm text-white/70"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.7 }}
                >
                  <span className="flex items-center gap-2 bg-white/5 backdrop-blur px-4 py-2 rounded-full">
                    <FaCalendarAlt /> {post.date}
                  </span>
                  <span className="flex items-center gap-2 bg-white/5 backdrop-blur px-4 py-2 rounded-full">
                    <FaClock /> {post.readTime}
                  </span>
                </motion.div>
              </motion.div>

              {/* Title with Stagger Animation */}
              <motion.h1 
                className="text-5xl lg:text-7xl xl:text-8xl font-rajdhani font-bold mb-8 leading-tight text-white"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
              >
                <span className="inline-block">
                  {post.title.split(' ').map((word, index) => (
                    <motion.span
                      key={index}
                      className="inline-block mr-3 text-white"
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ 
                        duration: 0.5, 
                        delay: 0.7 + index * 0.05,
                        ease: "easeOut"
                      }}
                    >
                      {word}
                    </motion.span>
                  ))}
                </span>
              </motion.h1>

              {/* Excerpt with Fade In */}
              <motion.p 
                className="text-xl lg:text-2xl text-white/80 max-w-3xl mb-10 leading-relaxed font-opensans"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 1, ease: "easeOut" }}
              >
                {post.excerpt}
              </motion.p>

              {/* Author & Share Section */}
              <motion.div 
                className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-8 border-t border-white/10"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.2 }}
              >
                {/* Author Info */}
                <div className="flex items-center gap-4">
                  <motion.div 
                    className="w-16 h-16 bg-gradient-to-br from-white/20 to-white/5 rounded-full flex items-center justify-center text-2xl font-bold backdrop-blur"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                  >
                    {post.author.charAt(0)}
                  </motion.div>
                  <div>
                    <div className="text-sm text-white/60 mb-1">Written by</div>
                    <div className="text-lg font-bold text-white">{post.author}</div>
                  </div>
                </div>

                {/* Share Buttons with 3D Effect */}
                <div className="flex items-center gap-3">
                  <span className="text-sm text-white/70 mr-2">Share:</span>
                  {[
                    { icon: FaFacebookF, platform: 'facebook', color: 'hover:bg-blue-600' },
                    { icon: FaTwitter, platform: 'twitter', color: 'hover:bg-sky-500' },
                    { icon: FaLinkedinIn, platform: 'linkedin', color: 'hover:bg-blue-700' }
                  ].map(({ icon: Icon, platform, color }, index) => (
                    <motion.button
                      key={platform}
                      onClick={() => handleShare(platform)}
                      className={`w-12 h-12 bg-white/10 backdrop-blur hover:bg-white/20 ${color} rounded-full flex items-center justify-center transition-all duration-300 group`}
                      aria-label={`Share on ${platform}`}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1.3 + index * 0.1, type: "spring" }}
                      whileHover={{ 
                        scale: 1.15, 
                        y: -5,
                        boxShadow: '0 10px 25px rgba(255,255,255,0.2)'
                      }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Icon className="text-lg group-hover:scale-110 transition-transform" />
                    </motion.button>
                  ))}
                </div>
              </motion.div>

              {/* Tags with Floating Animation */}
              <motion.div 
                className="flex flex-wrap gap-3 mt-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.4 }}
              >
                {post.tags.map((tag, index) => (
                  <motion.span
                    key={index}
                    className="bg-white/10 backdrop-blur border border-white/20 text-white text-sm px-4 py-2 rounded-full font-semibold hover:bg-gradient-to-r hover:from-white hover:to-gray-100 hover:text-[#22446b] transition-all duration-300 cursor-pointer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5 + index * 0.1 }}
                    whileHover={{ 
                      scale: 1.05, 
                      y: -3,
                      boxShadow: '0 5px 15px rgba(255,255,255,0.2)'
                    }}
                  >
                    #{tag}
                  </motion.span>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Animated Bottom Wave */}
          <motion.div 
            className="absolute bottom-0 left-0 right-0 z-20"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.2, delay: 1.6 }}
          >
            <svg className="w-full h-24" viewBox="0 0 1200 120" preserveAspectRatio="none">
              <motion.path 
                d="M0,60 C300,100 900,20 1200,60 L1200,120 L0,120 Z" 
                fill="white"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, delay: 1.7 }}
              />
            </svg>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 1 }}
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="flex flex-col items-center gap-2 text-white/60"
            >
              <span className="text-xs uppercase tracking-wider">Scroll to Read</span>
              <motion.div 
                className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2"
                whileHover={{ borderColor: 'rgba(255,255,255,0.6)' }}
              >
                <motion.div 
                  className="w-1.5 h-1.5 bg-white rounded-full"
                  animate={{ y: [0, 12, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* Article Content */}
        <motion.article 
          className="py-16 bg-white"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <div className="container mx-auto px-6 lg:px-8 max-w-4xl">
            <div className="prose prose-lg max-w-none">
              {content}
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center gap-3 flex-wrap">
                <FaTag className="text-gray-400" />
                {post.tags.map((tag, index) => (
                  <span 
                    key={index}
                    className="bg-gray-100 text-gray-700 px-4 py-2 rounded-full text-sm font-semibold hover:bg-gradient-to-r hover:from-[#22446b] hover:to-[#338edc] hover:text-white transition-all duration-300 cursor-pointer"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </motion.article>

        {/* Related Posts */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-6 lg:px-8">
            <h2 className="text-3xl font-rajdhani font-bold mb-10">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {blogPosts
                .filter(p => p.id !== post.id && p.category === post.category)
                .slice(0, 3)
                .map((relatedPost) => (
                  <Link 
                    key={relatedPost.id}
                    to={`/blog/${relatedPost.slug}`}
                    className="group"
                  >
                    <div className="bg-white rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                      <div className="h-48 overflow-hidden">
                        <img 
                          src={relatedPost.image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-6">
                        <h3 className="font-rajdhani font-bold text-xl mb-2 text-[#22446b] group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#22446b] group-hover:to-[#338edc] transition-all">
                          {relatedPost.title}
                        </h3>
                        <p className="text-gray-600 text-sm line-clamp-2">
                          {relatedPost.excerpt}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPost;
